import DataGrid, { Column, IColumnProps, IStateStoringProps, Item, Pager, Paging, SearchPanel, Toolbar } from "devextreme-react/data-grid";
import { dxButtonOptions } from "devextreme/ui/button";
import * as React from "react";
import { useCallback, useMemo, useRef } from "react";
import { generatePath, Link, useHistory } from "react-router-dom";
import GenericDataGrid from "../../components/GenericDataGrid";
import DirtyPrompt from "../../components/DirtyPrompt";

export interface IPageWithDataGridEditorProps {
    apiUrl: string;

    pageTitle?: string,

    /**
     * (optioneel) Deze kolom is een hyperlink als editUrl ingevuld werd.
     * */
    firstColumn?: string | IColumnProps;

    /**
     * extra (optionele) kolommen
     * */
    columns?: IColumnProps[];

    /**
     * enkel invullen als er een aparte editpagina is voor nieuwe of bestaande items.
     * */
    editPath?: string;

    onSaved?: () => void;

    stateStoreKey?: string;
    allowAdd?: boolean
}

const PageWithDataGridEditor = <TModel extends { id?: number }>({ apiUrl, pageTitle, firstColumn, columns, editPath, onSaved, stateStoreKey, allowAdd }: IPageWithDataGridEditorProps) => {
    const history = useHistory();
    
    const createUrlForItem = useCallback((id: number) => generatePath(editPath || "", { id }), [editPath]);
    const dgRef = useRef<DataGrid<TModel, number>>(null);

    const addBtnOptions = useMemo((): dxButtonOptions => ({
        onClick: () => history.push(createUrlForItem(0))
    }), [history, createUrlForItem])

    const renderFirstColomn = useCallback((data: { data: TModel, value: any, text: any }) => {
        const { id } = data.data;
        return <Link to={createUrlForItem(id || 0)}>{data.value || "< Geen gegevens beschikbaar! >"}</Link>;
    }, [createUrlForItem])

    const isDirtyHandler = useCallback(() => dgRef.current?.instance.hasEditData(), [])

    const stateStoring = useMemo((): IStateStoringProps | undefined => (stateStoreKey && { storageKey: stateStoreKey, type: "localStorage", enabled: true }) || undefined, [stateStoreKey])
    const allowedPageSizes = useMemo(() => [10, 20, 50, 100], []);
    return <>
        <DirtyPrompt when={isDirtyHandler} />
        {pageTitle && <h2>{pageTitle}</h2>}
        <GenericDataGrid<TModel, number>
            dgRef={dgRef}
            apiUrl={apiUrl}
            allowAdd={allowAdd === undefined || allowAdd}
            allowDelete
            allowUpdate={!editPath}
            onSaaved={onSaved}
            stateStoring={stateStoring}                        
        >
            {firstColumn && ((typeof firstColumn === "string"
                && <Column dataField={firstColumn} defaultSortIndex={0} defaultSortOrder="asc" cellRender={(editPath && renderFirstColomn) || undefined}  />)
                ||  <Column {...firstColumn} cellRender={(editPath && renderFirstColomn) || undefined} />)}

            {columns?.map((col, idx) => <Column key={idx} {...col} />)}


            {editPath && <Toolbar>
                <Item name="addRowButton" options={addBtnOptions} />
                <Item name="revertButton" />
                <Item name="saveButton" />
                <Item name="searchPanel" />
            </Toolbar>}

            <SearchPanel visible highlightSearchText />
            <Pager visible showPageSizeSelector showNavigationButtons showInfo allowedPageSizes={allowedPageSizes} />

            </GenericDataGrid>
        </>;
}

export default PageWithDataGridEditor;